import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

const LANG_KEY = 'sv_lang';
const COOKIE_DOMAIN = '.seebaby.ai';

const COUNTRY_LANG_MAP: Record<string, string> = {
  US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en',
  MX: 'es', ES: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
  EC: 'es', GT: 'es', CU: 'es', DO: 'es', HN: 'es', SV: 'es', NI: 'es',
  CR: 'es', PA: 'es', UY: 'es', PR: 'es', PY: 'es', BO: 'es',
  FR: 'fr', BE: 'fr', SN: 'fr', HT: 'fr', CI: 'fr', CM: 'fr',
  MG: 'fr', ML: 'fr', BF: 'fr', NE: 'fr', TD: 'fr', GN: 'fr',
  DE: 'de', AT: 'de', CH: 'de', LI: 'de', LU: 'de',
};

const SUPPORTED_LANGS = ['en', 'fr', 'es', 'de'];

@Injectable({ providedIn: 'root' })
export class LanguageService {
  constructor(private translate: TranslateService) {
    this.translate.addLangs(SUPPORTED_LANGS);
    this.translate.setDefaultLang('en');
    this.translate.use('en');
  }

  async initLanguage(): Promise<void> {
    const stored = localStorage.getItem(LANG_KEY) || this.getCookie(LANG_KEY);
    if (stored && SUPPORTED_LANGS.includes(stored)) {
      this.translate.use(stored);
      return;
    }
    try {
      const resp = await fetch('/cdn-cgi/trace');
      const text = await resp.text();
      const match = text.match(/loc=(\w+)/);
      if (match) {
        const lang = COUNTRY_LANG_MAP[match[1]] || 'en';
        this.applyLanguage(lang);
        return;
      }
    } catch {}
    this.applyLanguage('en');
  }

  switchLanguage(code: string): void {
    if (SUPPORTED_LANGS.includes(code)) {
      this.applyLanguage(code);
    }
  }

  getCurrentLang(): string {
    return this.translate.currentLang || 'en';
  }

  private applyLanguage(lang: string): void {
    this.translate.use(lang);
    localStorage.setItem(LANG_KEY, lang);
    this.setCookie(LANG_KEY, lang, 365, COOKIE_DOMAIN);
  }

  private setCookie(name: string, value: string, days: number, domain: string): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${value};expires=${expires};path=/;domain=${domain};SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? match[1] : null;
  }
}
